import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import './App.css';

// Hooks
import { useUserProgress, readOnboardingLock } from './hooks/useUserProgress';
import { useTranslation } from './hooks/useTranslation';
import { AuthProvider, hasUserEverBeenAuthenticatedOnDevice } from './hooks/useAuth';

// Components
import BottomNav from './components/BottomNav';
import NavTop from './components/NavTop';
import LevelUpCelebration from './components/LevelUpCelebration';
import XPGainAnimation from './components/XPGainAnimation';
import RouteSEO from './components/RouteSEO';
import ProtectedRoute from './components/ProtectedRoute';
import GlobalFooter from './components/GlobalFooter';
import FeedbackPill from './components/FeedbackPill';
import AppRuntimeBoundary from './components/AppRuntimeBoundary';
import CozyPageWrapper from './components/CozyPageWrapper';
import ErrorBoundary from './components/ErrorBoundary';
import AppLoader from './components/AppLoader';
import LoveColobongoNudge from './components/LoveColobongoNudge';
import LanguageStep from './components/LanguageStep';

// Pages
import Onboarding from './pages/Onboarding';
import Home from './pages/Home';
import { COOKIE_CONSENT_EVENTS, isNonEssentialCookiesAllowed } from './lib/cookieConsent';
import { initializeOptionalAnalytics, stopOptionalAnalytics } from './services/optionalAnalytics';
import { playSound, SOUNDS } from './utils/sounds';
import { vibrate, HAPTICS } from './utils/haptics';
import { devLog } from './utils/devLog';

// Recuperation automatique des erreurs de chargement de chunk. Quand un
// deploiement remplace les chunks hashes, un onglet ouvert avant le deploy
// reference des chunks qui n'existent plus -> l'import() rejette. On force
// alors UN rechargement (garde sessionStorage anti-boucle) pour recuperer la
// version a jour. Au reload, le chunk se charge normalement.
const CHUNK_RELOAD_KEY = 'colobongo_chunk_reload_once';

function lazyWithRetry(importer) {
  return lazy(async () => {
    try {
      const mod = await importer();
      try {
        window.sessionStorage.removeItem(CHUNK_RELOAD_KEY);
      } catch (_err) {
        // best-effort
      }
      return mod;
    } catch (error) {
      let alreadyReloaded = false;
      try {
        alreadyReloaded = !!window.sessionStorage.getItem(CHUNK_RELOAD_KEY);
      } catch (_err) {
        alreadyReloaded = false;
      }
      if (!alreadyReloaded) {
        try {
          window.sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
        } catch (_err) {
          // best-effort
        }
        window.location.reload();
        // Promesse qui ne se resout jamais : empeche tout rendu avant que le
        // reload prenne effet.
        return new Promise(() => {});
      }
      // Deja tente une fois : on relance l'erreur pour que l'ErrorBoundary
      // affiche la carte "Oups" avec bouton Rafraichir.
      throw error;
    }
  });
}

const CookieConsent = lazyWithRetry(() => import('./components/CookieConsent'));
const OnboardingCarousel = lazyWithRetry(() => import('./components/OnboardingCarousel'));
const LoginPromptModal = lazyWithRetry(() => import('./components/LoginPromptModal'));
const ArtistNamePromptModal = lazyWithRetry(() => import('./components/ArtistNamePromptModal'));
const PopupChoixContributeur = lazyWithRetry(() => import('./components/PopupChoixContributeur'));
const BoiteALettres = lazyWithRetry(() => import('./components/BoiteALettres'));
const WheelPage = lazyWithRetry(() => import('./pages/WheelPage'));
const ActiveChallenge = lazyWithRetry(() => import('./pages/ActiveChallenge'));
const PalettesPage = lazyWithRetry(() => import('./pages/PalettesPage'));
const StudioPage = lazyWithRetry(() => import('./pages/StudioPage'));
const ColoringProjectsList = lazyWithRetry(() => import('./components/ColoringProjectsList'));
const ColoringProjectDetailPage = lazyWithRetry(() => import('./pages/ColoringProjectDetailPage'));
const ScanPage = lazyWithRetry(() => import('./pages/ScanPage'));
const ProfilePage = lazyWithRetry(() => import('./pages/ProfilePage'));
const SettingsPage = lazyWithRetry(() => import('./pages/SettingsPage'));
const InstallAppPage = lazyWithRetry(() => import('./pages/InstallAppPage'));
const AuthPage = lazyWithRetry(() => import('./pages/AuthPage'));
const AuthCallbackPage = lazyWithRetry(() => import('./pages/AuthCallbackPage'));
const ResetPasswordPage = lazyWithRetry(() => import('./pages/ResetPasswordPage'));
const LightHelperPage = lazyWithRetry(() => import('./pages/LightHelperPage.js'));
const GalleryPage = lazyWithRetry(() => import('./pages/GalleryPage'));
const CompletedChallengesPage = lazyWithRetry(() => import('./pages/CompletedChallengesPage'));
const ApplicationColoriagePapierPage = lazyWithRetry(() => import('./pages/ApplicationColoriagePapierPage'));
const ScanColoriageIAPage = lazyWithRetry(() => import('./pages/ScanColoriageIAPage'));

// Legal pages
const PrivacyPolicyPage = lazyWithRetry(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazyWithRetry(() => import('./pages/TermsOfServicePage'));
const RemboursementPage = lazyWithRetry(() => import('./pages/RemboursementPage'));
const CookiesPolicyPage = lazyWithRetry(() => import('./pages/CookiesPolicyPage'));

// Tool pages
const ShadeBuilderPage = lazyWithRetry(() => import('./pages/ShadeBuilderPage'));
const TexturesPage = lazyWithRetry(() => import('./pages/TexturesPage.js'));
const TextureDetail = lazyWithRetry(() => import('./components/TextureDetail.js'));
const EffectsPage = lazyWithRetry(() => import('./pages/EffectsPage'));
const EffectDetail = lazyWithRetry(() => import('./pages/EffectDetail'));
const TextureScan = lazyWithRetry(() => import('./pages/TextureScan'));
const TextureResult = lazyWithRetry(() => import('./pages/TextureResult'));
const StudioInspirationPage = lazyWithRetry(() => import('./pages/StudioInspirationPage'));
const AdminShowcasePage = lazyWithRetry(() => import('./pages/AdminShowcasePage'));
// CODEX_PROMPT_83 : panneau admin de gestion des defis mensuels
const AdminMonthlyChallengesPage = lazyWithRetry(() => import('./pages/AdminMonthlyChallengesPage'));
const AdminEffectsPage = lazyWithRetry(() => import('./pages/AdminEffectsPage'));
const AdminTexturesPage = lazyWithRetry(() => import('./pages/AdminTexturesPage'));
const AdminWhatsNewPage = lazyWithRetry(() => import('./pages/AdminWhatsNewPage'));
const WallPage = lazyWithRetry(() => import('./pages/WallPage'));
const FrigoPublicPage = lazyWithRetry(() => import('./pages/FrigoPublicPage'));
const DeviensCreateurPage = lazyWithRetry(() => import('./pages/DeviensCreateurPage'));
const SoutenirPage = lazyWithRetry(() => import('./pages/SoutenirPage'));

// Games
const GamesPage = lazyWithRetry(() => import('./pages/GamesPage'));
const ColorMemoryGame = lazyWithRetry(() => import('./games/color-memory/ColorMemoryGame'));
const ShadeMatchGame = lazyWithRetry(() => import('./games/shade-match/ShadeMatchGame'));





const APP_SYNC_GATE_TIMEOUT_MS = 12000;
const ACTIVE_SCAN_SESSION_KEY = 'colobongo_active_scan_v1';
const SEO_GATE_BYPASS_PATHS = new Set([
  '/privacy',
  '/terms',
  '/remboursement',
  '/cookies',
  '/light-helper',
  '/textures',
  '/effets'
]);

function RouteLoadingFallback({ language = 'fr' }) {
  return (
    <div className="page-content px-4 pt-6 max-w-md mx-auto">
      <div className="cozy-card p-4 border border-[var(--cb-border-hover)] bg-[var(--cb-cream)]">
        <p className="text-sm font-semibold text-[color:var(--cb-text)]">
          {language === 'fr'
            ? 'Chargement en cours...'
            : 'Loading...'}
        </p>
      </div>
    </div>
  );
}

function ScrollToTopOnRouteChange() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  return null;
}

function AnimatedRoutes({ userProgress, translation, syncGateTimedOut = false, onSyncGateTimeoutChange }) {
  const location = useLocation();

  const {
    progress,
    levelInfo,
    addXP,
    awardDailyXP,
    isSyncing,
    progressSource,
    isHydrated,
    authLoading,
    userId,
    setActiveChallenge,
    completeChallenge,
    updateStreak,
    setSelectedBrands,
    setSkillLevel,
    completeOnboarding,
    resetProgress,
    setProgress
  } = userProgress;

  const { t, language, setLanguage, toggleLanguage } = translation;
  const navigate = useNavigate();

  // Derived state needed early (before hooks that reference them)
  const canRenderMainUI = isHydrated || syncGateTimedOut;
  const isConnectedUser = Boolean(userId);

  const [showLevelUp, setShowLevelUp] = useState(false);
  const [levelUpData, setLevelUpData] = useState(null);
  const [xpGain, setXPGain] = useState(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  // Sequence d'onboarding : etape 1 (langue) et etape 3 (carrousel)
  const [languageChosen, setLanguageChosen] = useState(() => {
    if (typeof window === 'undefined') return true;
    try {
      return !!window.localStorage.getItem('colobongo_language_chosen');
    } catch (_e) {
      return false;
    }
  });

  const [carrouselDone, setCarrouselDone] = useState(() => {
    if (typeof window === 'undefined') return true;
    try {
      return !!window.localStorage.getItem('colobongo_carrousel_done');
    } catch (_e) {
      return false;
    }
  });

  const handleLanguageChosen = useCallback((lang) => {
    setLanguage(lang);
    try {
      window.localStorage.setItem('colobongo_language_chosen', '1');
    } catch (_e) {
      // best-effort
    }
    setLanguageChosen(true);
  }, [setLanguage]);

  const handleCarrouselDone = useCallback(() => {
    setCarrouselDone(true);
    // colobongo_carrousel_done est deja pose par OnboardingCarousel lui-meme
  }, []);

  // Replay tutoriel depuis Reglages : montre le carrousel par-dessus l'app
  // sans toucher au flag colobongo_carrousel_done ni a la config utilisateur.
  const [isReplayingTutorial, setIsReplayingTutorial] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onReplay = () => setIsReplayingTutorial(true);
    window.addEventListener('colobongo:replay-tour', onReplay);
    return () => window.removeEventListener('colobongo:replay-tour', onReplay);
  }, []);
  const handleReplayDone = useCallback(() => {
    setIsReplayingTutorial(false);
  }, []);

  const handleLoginPromptLogin = () => {
    setShowLoginPrompt(false);
    navigate('/auth');
  };

  const handleLoginPromptGuestContinue = () => {
    setShowLoginPrompt(false);
  };

  // Detect returning user who was previously authenticated on this device
  // If they were ever logged in (even if signed out now), skip onboarding and login prompt
  const wasEverAuthenticatedOnDevice = React.useMemo(() => {
    return hasUserEverBeenAuthenticatedOnDevice();
  }, []);

  // Detect returning guest: has guest progress in localStorage but no account
  // This means they visited before as a guest, so we should prompt them to sign in
  const hasReturningGuestProgress = React.useMemo(() => {
    if (!isHydrated || !canRenderMainUI) return false;
    if (isConnectedUser) return false; // Already signed in
    if (wasEverAuthenticatedOnDevice) return false; // Was authenticated before → handled separately
    if (progress.onboardingComplete) return false; // Already did onboarding
    if (progress.challengesCompleted === undefined || progress.challengesCompleted === 0) return false; // Never did anything
    if (!progress.selectedBrands || progress.selectedBrands.length === 0) return false; // Never selected brands
    return true;
  }, [isHydrated, canRenderMainUI, isConnectedUser, wasEverAuthenticatedOnDevice, progress.onboardingComplete, progress.challengesCompleted, progress.selectedBrands]);

  // Show login prompt when a returning guest (with previous activity) visits the app
  useEffect(() => {
    if (!hasReturningGuestProgress) return;
    if (!isHydrated || !canRenderMainUI) return;
    // Only show once per session
    setShowLoginPrompt(true);
  }, [hasReturningGuestProgress, isHydrated, canRenderMainUI]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      devLog.info('[APP_GATE] snapshot', {
        authLoading,
        userId,
        isHydrated,
        isSyncing,
        progressSource,
        syncGateTimedOut,
        pathname: location.pathname,
      });
    }
  }, [authLoading, userId, isHydrated, isSyncing, progressSource, syncGateTimedOut, location.pathname]);

  useEffect(() => {
    if (isHydrated) {
      if (syncGateTimedOut) {
        devLog.info('[APP_GATE] hydration recovered after timeout gate');
        if (typeof onSyncGateTimeoutChange === 'function') {
          onSyncGateTimeoutChange(false);
        }
      }
      return undefined;
    }

    if (syncGateTimedOut) {
      return undefined;
    }

    devLog.info('[APP_GATE] full-screen sync gate armed', {
      timeoutMs: APP_SYNC_GATE_TIMEOUT_MS,
      authLoading,
      userId,
      progressSource,
      isSyncing,
    });

    const timer = setTimeout(() => {
      devLog.warn('[APP_GATE] full-screen sync gate timeout reached, enabling non-blocking fallback UI', {
        authLoading,
        userId,
        progressSource,
        isSyncing,
      });
      if (typeof onSyncGateTimeoutChange === 'function') {
        onSyncGateTimeoutChange(true);
      }
    }, APP_SYNC_GATE_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [isHydrated, syncGateTimedOut, authLoading, userId, progressSource, isSyncing, onSyncGateTimeoutChange]);

  // Level up
  useEffect(() => {
    if (!isHydrated) return;
    if (progress._leveledUp && progress._newLevel) {
      setLevelUpData(progress._newLevel);
      setShowLevelUp(true);
      setProgress(prev => ({
        ...prev,
        _leveledUp: false,
        _newLevel: null
      }));
    }
  }, [isHydrated, progress._leveledUp, progress._newLevel, setProgress]);

  // Streak
  useEffect(() => {
    if (!isHydrated) return;
    if (progress.onboardingComplete) {
      updateStreak();
    }
  }, [isHydrated, progress.onboardingComplete, updateStreak]);

  // XP handler
  const handleAddXP = (amount) => {
    setXPGain({ amount, id: Date.now() });
    addXP(amount, { source: `route:${location.pathname}` });
  };

  // Daily XP
  useEffect(() => {
    if (!isHydrated) return undefined;
    if (progress.onboardingComplete) {
      const timer = setTimeout(() => {
        const granted = awardDailyXP(10);
        if (granted) {
          setXPGain({ amount: 10, id: Date.now() });
        }
      }, 1500);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isHydrated, progress.onboardingComplete, progress.lastDailyXPDate, awardDailyXP]);

  const isOnScanRoute = location.pathname === '/scan';
  const normalizedPath = location.pathname === '/'
    ? '/'
    : location.pathname.replace(/\/+$/, '');
  const isHomeRoute = normalizedPath === '/';
  const isTextureDetailPage = /^\/textures\/[^/]+$/.test(normalizedPath);
  const isEffectDetailPage = /^\/effets\/[^/]+$/.test(normalizedPath);
  const isSeoGateBypassRoute = SEO_GATE_BYPASS_PATHS.has(normalizedPath) || isTextureDetailPage || isEffectDetailPage;
  const shouldSkipOnboardingPrompt =
    isHomeRoute
    && Boolean(location.state?.skipOnboardingPrompt);
  // Read localStorage lock immediately to prevent onboarding re-showing before cloud hydration
  const onboardingLockedByStorage = readOnboardingLock(userId);
  const isOnboardingStateReliable = isHydrated && (!isConnectedUser || progressSource === 'cloud');
  const shouldShowOnboarding =
    isHomeRoute &&
    !shouldSkipOnboardingPrompt &&
    isOnboardingStateReliable &&
    !progress.onboardingComplete &&
    !onboardingLockedByStorage &&
    !wasEverAuthenticatedOnDevice; // Skip onboarding if user was ever authenticated on this device
  const shouldHoldOnboardingDecision =
    isHomeRoute &&
    !shouldSkipOnboardingPrompt &&
    isConnectedUser &&
    !isOnboardingStateReliable &&
    !progress.onboardingComplete &&
    !onboardingLockedByStorage &&
    !wasEverAuthenticatedOnDevice; // Never hold if user was previously authenticated
  const onboardingGateDecision = shouldHoldOnboardingDecision
    ? 'hold'
    : shouldShowOnboarding
      ? 'show_onboarding'
      : 'show_home';

  const welcomeGateReason = shouldHoldOnboardingDecision
    ? 'connected_state_not_reliable_yet'
    : shouldShowOnboarding
      ? 'onboarding_not_completed_in_reliable_state'
      : shouldSkipOnboardingPrompt
        ? 'scan_exit_skip_onboarding_prompt'
        : 'onboarding_completed_or_not_needed';

  let isActiveScanFlow = false;
  if (isOnScanRoute && typeof window !== 'undefined') {
    try {
      const raw = window.sessionStorage.getItem(ACTIVE_SCAN_SESSION_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      isActiveScanFlow = Boolean(parsed?.hasCapture);
    } catch (_error) {
      isActiveScanFlow = false;
    }
  }

  const shouldBypassBlockingGate = !isHydrated && !syncGateTimedOut && (isActiveScanFlow || isSeoGateBypassRoute);
  const isGateBlockingSafe = !isHydrated && !syncGateTimedOut && !shouldBypassBlockingGate;

  const navHiddenPrefixes = ['/splash', '/onboarding', '/auth'];
  const isNavHiddenRoute = navHiddenPrefixes.some(
    (prefix) => normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`)
  );
  const showNav =
    canRenderMainUI &&
    !isNavHiddenRoute &&
    !shouldShowOnboarding &&
    !shouldHoldOnboardingDecision;

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      devLog.info('[WELCOME_STATE] snapshot', {
        userId,
        isConnectedUser,
        isHydrated,
        progressSource,
        onboardingComplete: progress.onboardingComplete,
        isOnboardingStateReliable,
        syncGateTimedOut,
        pathname: location.pathname,
        timestamp: new Date().toISOString(),
      });
    }
  }, [
    userId,
    isConnectedUser,
    isHydrated,
    progressSource,
    progress.onboardingComplete,
    isOnboardingStateReliable,
    syncGateTimedOut,
    location.pathname,
  ]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      devLog.info('[WELCOME_GATE] decision', {
        decision: onboardingGateDecision,
        reason: welcomeGateReason,
        userId,
        isConnectedUser,
        isHydrated,
        progressSource,
        onboardingComplete: progress.onboardingComplete,
        isOnboardingStateReliable,
        timestamp: new Date().toISOString(),
      });
    }
  }, [
    onboardingGateDecision,
    welcomeGateReason,
    userId,
    isConnectedUser,
    isHydrated,
    progressSource,
    progress.onboardingComplete,
    isOnboardingStateReliable,
  ]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      devLog.info('[WELCOME_SOURCE] resolved', {
        userId,
        isConnectedUser,
        progressSource,
        isOnboardingStateReliable,
        onboardingComplete: progress.onboardingComplete,
        decision: onboardingGateDecision,
        timestamp: new Date().toISOString(),
      });
    }
  }, [
    userId,
    isConnectedUser,
    progressSource,
    isOnboardingStateReliable,
    progress.onboardingComplete,
    onboardingGateDecision,
  ]);

  if (shouldBypassBlockingGate) {
    devLog.info('[APP_GATE] bypassing blocking sync gate due to active scan flow', {
      authLoading,
      userId,
      progressSource,
      isSyncing,
      pathname: location.pathname,
    });
  }

  if (isGateBlockingSafe) {
    devLog.info('[APP_GATE] rendering blocking sync screen', {
      reason: 'isHydrated_false_before_timeout',
      authLoading,
      userId,
      progressSource,
      isSyncing,
    });
    return <RouteLoadingFallback language={language} />;
  }

  return (
    <>
      <RouteSEO pathname={location.pathname} language={language} />
      {showNav && <NavTop language={language} />}
      <main id="main-content" role="main">

      {!isHydrated && syncGateTimedOut && (
        <div className="page-content px-4 pt-4 pb-0 max-w-md mx-auto">
          <div className="cozy-card p-4 border border-[rgba(232,168,124,0.35)] bg-[rgba(255,248,240,0.9)]">
            <p className="text-sm font-semibold text-[color:var(--cb-text)]">
              {language === 'fr'
                ? "On continue en douceur pendant la synchronisation."
                : "Temporary mode enabled: continuing while sync catches up."}
            </p>
            <p className="text-xs mt-1 text-[color:var(--cb-text-muted)]">
              {language === 'fr'
                ? "Tu peux continuer sans blocage, tes donnees se remettront a jour bientôt."
                : "Current source: local cache. You can keep going without blocking."}
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-3 px-3 py-1.5 rounded-full text-xs font-semibold bg-[color:var(--cb-secondary)] text-[color:var(--cb-text)] border border-[rgba(61,61,61,0.12)]"
            >
              {language === 'fr' ? 'Réessayer la synchronisation' : 'Retry sync'}
            </button>
          </div>
        </div>
      )}

      <ErrorBoundary language={language}>
      <Suspense fallback={<CozyPageWrapper><RouteLoadingFallback language={language} /></CozyPageWrapper>}>
      {/* mode="wait" : l'ancienne page finit son exit AVANT que la nouvelle
          commence son entree. Sans cela, les deux pages se superposent dans
          le DOM pendant ~180ms — la photo hero du detail coloriage apparaissait
          par-dessus la liste de la page /carnet. */}
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>

          {/* HOME */}
          <Route path="/" element={
            <CozyPageWrapper>
              {!languageChosen ? (
                <LanguageStep language={language} onChoose={handleLanguageChosen} />
              ) : shouldHoldOnboardingDecision ? (
                <div className="page-content px-4 pt-4 pb-0 max-w-md mx-auto">
                  <div className="cozy-card p-4 border border-[rgba(232,168,124,0.35)] bg-[rgba(255,248,240,0.9)]">
                    <p className="text-sm font-semibold text-[color:var(--cb-text)]">
                      {language === 'fr'
                        ? "On verifie ton parcours pour eviter de te renvoyer au debut."
                        : "We're verifying your journey so we don't send you back to the start."}
                    </p>
                    <p className="text-xs mt-1 text-[color:var(--cb-text-muted)]">
                      {language === 'fr'
                        ? "Merci de patienter un instant, on chargé ton etat depuis le cloud."
                        : "Thanks for your patience, we're loading your state from the cloud."}
                    </p>
                    <button
                      type="button"
                      onClick={() => window.location.reload()}
                      className="mt-3 px-3 py-1.5 rounded-full text-xs font-semibold bg-[color:var(--cb-secondary)] text-[color:var(--cb-text)] border border-[rgba(61,61,61,0.12)]"
                    >
                      {language === 'fr' ? 'Réessayer' : 'Retry'}
                    </button>
                  </div>
                </div>
              ) : shouldShowOnboarding ? (
                  !carrouselDone ? (
                    <Suspense fallback={<RouteLoadingFallback language={language} />}>
                      <OnboardingCarousel
                        language={language}
                        onDone={handleCarrouselDone}
                      />
                    </Suspense>
                  ) : (
                    <Onboarding
                      t={t}
                      language={language}
                      setSelectedBrands={setSelectedBrands}
                      setSkillLevel={setSkillLevel}
                      completeOnboarding={completeOnboarding}
                    />
                  )
                ) : (
                  <Home
                    t={t}
                    language={language}
                    progress={progress}
                    levelInfo={levelInfo}
                    addXP={handleAddXP}
                    progressSource={progressSource}
                    isProgressSyncing={isSyncing}
                  />
                )}
            </CozyPageWrapper>
          } />

          {/* CORE */}
          <Route path="/wheel" element={<CozyPageWrapper><WheelPage t={t} language={language} setActiveChallenge={setActiveChallenge} addXP={handleAddXP} /></CozyPageWrapper>} />
          <Route path="/challenge" element={<CozyPageWrapper><ActiveChallenge t={t} language={language} progress={progress} setActiveChallenge={setActiveChallenge} /></CozyPageWrapper>} />
          <Route path="/palettes" element={<CozyPageWrapper><PalettesPage t={t} language={language} progress={progress} /></CozyPageWrapper>} />
          <Route path="/studio" element={<CozyPageWrapper><StudioPage t={t} language={language} /></CozyPageWrapper>} />
          <Route path="/studio/inspiration-ai" element={<CozyPageWrapper><StudioInspirationPage language={language} /></CozyPageWrapper>} />
          <Route path="/carnet" element={<CozyPageWrapper><ColoringProjectsList language={language} onProjectSelect={(id) => navigate(`/carnet/${id}`)} /></CozyPageWrapper>} />
          <Route path="/carnet/:projectId" element={<CozyPageWrapper><ColoringProjectDetailPage language={language} /></CozyPageWrapper>} />
          <Route path="/scan" element={<CozyPageWrapper><ScanPage t={t} language={language} progress={progress} addXP={handleAddXP} completeChallenge={completeChallenge} /></CozyPageWrapper>} />
          <Route path="/gallery" element={<CozyPageWrapper><GalleryPage t={t} language={language} /></CozyPageWrapper>} />
          <Route path="/completed-challenges" element={<CozyPageWrapper><CompletedChallengesPage language={language} /></CozyPageWrapper>} />
          <Route path="/profile" element={<CozyPageWrapper><ProfilePage t={t} language={language} progress={progress} levelInfo={levelInfo} progressSource={progressSource} isProgressSyncing={isSyncing} /></CozyPageWrapper>} />
          <Route path="/boite-lettres" element={<CozyPageWrapper><BoiteALettres language={language} /></CozyPageWrapper>} />
          <Route path="/frigo/:userId" element={<CozyPageWrapper><FrigoPublicPage language={language} /></CozyPageWrapper>} />
          <Route path="/settings" element={<CozyPageWrapper><SettingsPage t={t} language={language} setLanguage={setLanguage} toggleLanguage={toggleLanguage} progress={progress} setSelectedBrands={setSelectedBrands} resetProgress={resetProgress} /></CozyPageWrapper>} />
          <Route path="/install" element={<CozyPageWrapper><InstallAppPage language={language} /></CozyPageWrapper>} />
          <Route path="/auth" element={<CozyPageWrapper><AuthPage t={t} language={language} /></CozyPageWrapper>} />
          <Route path="/auth/callback" element={<AuthCallbackPage language={language} />} />
          <Route path="/reset-password" element={<ResetPasswordPage language={language} />} />
          <Route path="/light-helper" element={<CozyPageWrapper><LightHelperPage t={t} language={language} /></CozyPageWrapper>} />
          <Route path="/application-coloriage-papier" element={<CozyPageWrapper><ApplicationColoriagePapierPage language={language} /></CozyPageWrapper>} />
          <Route path="/scan-coloriage-ia" element={<CozyPageWrapper><ScanColoriageIAPage language={language} /></CozyPageWrapper>} />

          {/* LEGAL */}
          <Route path="/privacy" element={<CozyPageWrapper><PrivacyPolicyPage language={language} /></CozyPageWrapper>} />
          <Route path="/terms" element={<CozyPageWrapper><TermsOfServicePage language={language} /></CozyPageWrapper>} />
          <Route path="/remboursement" element={<CozyPageWrapper><RemboursementPage language={language} /></CozyPageWrapper>} />
          <Route path="/cookies" element={<CozyPageWrapper><CookiesPolicyPage language={language} /></CozyPageWrapper>} />

          {/* TOOL */}
          <Route path="/shade-builder" element={
            <CozyPageWrapper>
              <ShadeBuilderPage t={t} language={language} progress={progress} />
            </CozyPageWrapper>
          } />

          {/* TEXTURES */}
          <Route path="/textures" element={<CozyPageWrapper><TexturesPage t={t} language={language} /></CozyPageWrapper>} />
          <Route path="/textures/:slug" element={<CozyPageWrapper><TextureDetail language={language} /></CozyPageWrapper>} />
          <Route path="/textures/:slug/scan" element={<CozyPageWrapper><TextureScan language={language} addXP={handleAddXP} /></CozyPageWrapper>} />
          <Route path="/textures/:slug/result" element={<CozyPageWrapper><TextureResult language={language} progress={progress} /></CozyPageWrapper>} />
          {/* CODEX_PROMPT_87 : Effets & Techniques (porte Apprendre) */}
          <Route path="/effets" element={<CozyPageWrapper><EffectsPage language={language} /></CozyPageWrapper>} />
          <Route path="/effets/:slug" element={<CozyPageWrapper><EffectDetail language={language} /></CozyPageWrapper>} />
          <Route path="/mur" element={<CozyPageWrapper><WallPage language={language} /></CozyPageWrapper>} />
          <Route path="/deviens-createur" element={<CozyPageWrapper><DeviensCreateurPage language={language} /></CozyPageWrapper>} />
          <Route path="/soutenir" element={<CozyPageWrapper><SoutenirPage language={language} /></CozyPageWrapper>} />

          {/* GAMES */}
          <Route path="/games" element={<CozyPageWrapper><GamesPage language={language} /></CozyPageWrapper>} />
          <Route path="/games/color-memory" element={<CozyPageWrapper><ColorMemoryGame language={language} /></CozyPageWrapper>} />
          <Route path="/games/shade-match" element={<CozyPageWrapper><ShadeMatchGame language={language} /></CozyPageWrapper>} />
          <Route path="/showcase/coloring-of-the-month" element={<CozyPageWrapper><WallPage language={language} /></CozyPageWrapper>} />
          <Route path="/admin-showcase" element={
            <ProtectedRoute requireAdmin>
              <CozyPageWrapper><AdminShowcasePage language={language} /></CozyPageWrapper>
            </ProtectedRoute>
          } />
          {/* CODEX_PROMPT_83 : gestion des defis mensuels (admin) */}
          <Route path="/admin/defi-mensuel" element={
            <ProtectedRoute requireAdmin>
              <CozyPageWrapper><AdminMonthlyChallengesPage language={language} /></CozyPageWrapper>
            </ProtectedRoute>
          } />
          {/* CODEX_PROMPT_87 : gestion des effets & techniques (admin) */}
          <Route path="/admin/effets" element={
            <ProtectedRoute requireAdmin>
              <CozyPageWrapper><AdminEffectsPage language={language} /></CozyPageWrapper>
            </ProtectedRoute>
          } />
          {/* Gestion des tutoriels Motif & Texture (admin) */}
          <Route path="/admin/textures" element={
            <ProtectedRoute requireAdmin>
              <CozyPageWrapper><AdminTexturesPage language={language} /></CozyPageWrapper>
            </ProtectedRoute>
          } />
          {/* CODEX_PROMPT_88 : gestion de la section Quoi de neuf (admin) */}
          <Route path="/admin/quoi-de-neuf" element={
            <ProtectedRoute requireAdmin>
              <CozyPageWrapper><AdminWhatsNewPage language={language} /></CozyPageWrapper>
            </ProtectedRoute>
          } />

        </Routes>
      </AnimatePresence>
      </Suspense>
      </ErrorBoundary>
      </main>

      <GlobalFooter language={language} withBottomNav={showNav} />

      {/* NAV */}
      {showNav && <BottomNav t={t} />}

      {/* Floating feedback pill (early access). Hidden on auth/onboarding
          where showNav is false to keep those screens distraction-free. */}
      {showNav && <FeedbackPill language={language} withBottomNav={showNav} />}

      {/* XP + LEVEL */}
      {showLevelUp && levelUpData && (
        <LevelUpCelebration
          newLevel={levelUpData.level}
          levelName={language === "fr" ? levelUpData.nameFr : levelUpData.name}
          t={t}
          onClose={() => setShowLevelUp(false)}
        />
      )}

      {xpGain && (
        <XPGainAnimation
          key={xpGain.id}
          amount={xpGain.amount}
          onComplete={() => setXPGain(null)}
        />
      )}

      {/* GLOBAL POPUPS */}
      <Suspense fallback={null}>
        <CookieConsent language={language} />
        <LoginPromptModal
          open={showLoginPrompt}
          language={language}
          onLogin={handleLoginPromptLogin}
          onGuestContinue={handleLoginPromptGuestContinue}
        />
        <ArtistNamePromptModal
          language={language}
          enabled={canRenderMainUI && !isGateBlockingSafe && !shouldShowOnboarding}
        />
        <PopupChoixContributeur
          language={language}
          enabled={canRenderMainUI && !isGateBlockingSafe && !shouldShowOnboarding}
        />
      </Suspense>
      <LoveColobongoNudge language={language} enabled={canRenderMainUI && !isGateBlockingSafe} />

      {/* Replay tutoriel (carrousel) declenche depuis Reglages */}
      {isReplayingTutorial && (
        <Suspense fallback={null}>
          <OnboardingCarousel language={language} onDone={handleReplayDone} />
        </Suspense>
      )}

    </>
  );
}

function AppContent() {
  const [syncGateTimedOut, setSyncGateTimedOut] = useState(false);
  const [loaderDismissed, setLoaderDismissed] = useState(false);
  const userProgress = useUserProgress();
  const translation = useTranslation();
  const hasPlayedEnterSoundRef = useRef(false);

  const isAppReady =
    (!userProgress?.authLoading && Boolean(userProgress?.isHydrated))
    || syncGateTimedOut;

  useEffect(() => {
    if (!isAppReady) return;
    const timer = setTimeout(() => setLoaderDismissed(true), 0);
    return () => clearTimeout(timer);
  }, [isAppReady]);

  const handleFirstInteractionSound = useCallback(() => {
    if (hasPlayedEnterSoundRef.current) return;
    hasPlayedEnterSoundRef.current = true;
    playSound(SOUNDS.ENTER);
  }, []);

  // Sound on first interaction
  useEffect(() => {
    const onFirstGesture = () => {
      handleFirstInteractionSound();
    };
    window.addEventListener('pointerdown', onFirstGesture, { passive: true });
    window.addEventListener('keydown', onFirstGesture);
    return () => {
      window.removeEventListener('pointerdown', onFirstGesture);
      window.removeEventListener('keydown', onFirstGesture);
    };
  }, [handleFirstInteractionSound]);

  // Haptic feedback on tap
  useEffect(() => {
    const onTap = (e) => {
      if (e.target.closest('button, a, [role="button"], [tabindex="0"]')) {
        vibrate(HAPTICS.TAP);
      }
    };
    document.addEventListener('pointerdown', onTap, { passive: true });
    return () => document.removeEventListener('pointerdown', onTap);
  }, []);

  // Optional analytics
  useEffect(() => {
    const maybeStartAnalytics = () => {
      if (isNonEssentialCookiesAllowed()) {
        initializeOptionalAnalytics();
      } else {
        stopOptionalAnalytics();
      }
    };
    maybeStartAnalytics();
    window.addEventListener(COOKIE_CONSENT_EVENTS.UPDATED, maybeStartAnalytics);
    return () => window.removeEventListener(COOKIE_CONSENT_EVENTS.UPDATED, maybeStartAnalytics);
  }, []);

  // Runtime error tracking
  useEffect(() => {
    const onRuntimeError = (event) => {
      devLog.error('[APP_GATE] runtime error', {
        message: event?.message || 'unknown',
        filename: event?.filename || null,
        lineno: event?.lineno || null,
        colno: event?.colno || null,
      });
    };
    const onUnhandledRejection = (event) => {
      const reason = event?.reason;
      devLog.error('[APP_GATE] unhandled promise rejection', {
        message: reason?.message || String(reason || 'unknown'),
      });
    };
    window.addEventListener('error', onRuntimeError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    return () => {
      window.removeEventListener('error', onRuntimeError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, []);

  return (
    <AppRuntimeBoundary language={translation.language === 'en' ? 'en' : 'fr'}>
      <AppLoader isAppReady={isAppReady} />
      {!loaderDismissed ? null : (
        <Router>
          <ScrollToTopOnRouteChange />
          <div className="min-h-screen cozy-app-shell">
            <AnimatedRoutes
              userProgress={userProgress}
              translation={translation}
              syncGateTimedOut={syncGateTimedOut}
              onSyncGateTimeoutChange={setSyncGateTimedOut}
            />
          </div>
        </Router>
      )}
    </AppRuntimeBoundary>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

